import { Controller } from "@hotwired/stimulus"
import { fetchLiveInput } from "../lib/cloudflare_live.js"

// Confirmed live playback takes priority over the saved show recording.
// Keep the most recently observed broadcast mounted when it ends.
export default class extends Controller {
  static targets = ["mount", "template", "status", "notice", "placeholder", "placeholderTemplate", "announcement", "episodeCaption", "recordingCaption", "liveCaption", "liveTitle", "liveDate", "nextShowTitle", "nextShowDate", "nextShow"]
  static values = { baseUrl: String, inputId: String, recordingId: String, scheduledShow: Object, broadcastShow: Object }

  connect() {
    this.stopped = false
    this.videoId = null
    this.lastLiveVideoId = null
    this.retryPlayback = false
    this.failures = 0
    if (this.recordingIdValue) {
      this.showPlayer(this.recordingIdValue)
      this.placeholderTarget.hidden = true
      this.episodeCaptionTarget.hidden = true
      this.recordingCaptionTarget.hidden = false
    } else {
      this.restorePlaceholder()
    }
    this.element.dataset.featuredVideoPlaying = String(!this.recordingIdValue)
    this.refresh()
  }

  disconnect() {
    this.stop()
  }

  stop() {
    this.stopped = true
    clearTimeout(this.timer)
    clearTimeout(this.requestTimeout)
    this.request?.abort()
    this.request = null
    delete this.element.dataset.live
    this.announcementTarget.textContent = ""
    this.episodeCaptionTarget.hidden = false
    this.recordingCaptionTarget.hidden = true
    this.liveCaptionTarget.hidden = true
    this.element.dataset.featuredVideoPlaying = String(!this.recordingIdValue)
    this.clearPlayer()
    this.placeholderTarget.replaceChildren()
    this.placeholderTarget.hidden = false
  }

  async refresh() {
    if (this.stopped || this.request) return
    clearTimeout(this.timer)
    const request = new AbortController()
    this.request = request
    this.requestTimeout = setTimeout(() => request.abort(), 8_000)
    try {
      const state = await fetchLiveInput(this.baseUrlValue, this.inputIdValue, request.signal)
      if (this.stopped || this.request !== request) return
      this.failures = 0
      this.noticeTargets.forEach(target => { target.hidden = true })
      const previousLive = this.element.dataset.live
      this.element.dataset.live = String(state.live)
      if (state.live && previousLive !== "true") {
        this.announcementTarget.textContent = "The broadcast is live."
      } else if (!state.live && previousLive === "true") {
        this.announcementTarget.textContent = "The live broadcast has ended."
      }
      this.liveCaptionTarget.hidden = !state.live
      if (state.live) {
        this.lastLiveVideoId = state.videoUID
        const show = this.broadcastShowValue.uid === state.videoUID ? this.broadcastShowValue : this.scheduledShowValue
        this.liveTitleTarget.textContent = show.title
        this.liveDateTarget.textContent = show.date
      }
      const videoId = state.live ? state.videoUID : this.lastLiveVideoId || this.recordingIdValue
      this.element.dataset.featuredVideoPlaying = String(!videoId)
      this.placeholderTarget.hidden = Boolean(videoId)
      this.episodeCaptionTarget.hidden = Boolean(videoId)
      this.recordingCaptionTarget.hidden = state.live || !videoId || videoId !== this.recordingIdValue
      if (videoId) {
        if (videoId !== this.videoId || this.retryPlayback) this.showPlayer(videoId)
      } else {
        this.restorePlaceholder()
        this.clearPlayer()
        this.statusTargets.forEach(target => {
          target.textContent = "Stay tuned. Playback will start automatically when the stream is available."
        })
      }
    } catch (error) {
      if (this.stopped || this.request !== request) return
      // Unknown is not offline: leave confirmed playback and status intact.
      this.failures++
      this.noticeTargets.forEach(target => {
        target.textContent = "Unable to check live status. Retrying automatically…"
        target.hidden = false
      })
    } finally {
      if (this.request === request) {
        clearTimeout(this.requestTimeout)
        this.request = null
        this.timer = setTimeout(() => this.refresh(), Math.min(60_000, 10_000 * 2 ** this.failures))
      }
    }
  }

  scheduleChanged({ detail }) {
    this.scheduledShowValue = { title: detail.title, date: detail.showId ? detail.label : "" }
    this.nextShowTitleTarget.textContent = detail.title
    this.nextShowDateTarget.textContent = detail.label
    this.nextShowTarget.hidden = Boolean(detail.showId && detail.showId === this.broadcastShowValue.id)
    if (this.element.dataset.live === "true" && this.videoId !== this.broadcastShowValue.uid) {
      this.liveTitleTarget.textContent = this.scheduledShowValue.title
      this.liveDateTarget.textContent = this.scheduledShowValue.date
    }
  }

  playbackFailed() {
    this.retryPlayback = true
    this.noticeTargets.forEach(target => {
      target.textContent = "Playback interrupted. Reconnecting automatically…"
      target.hidden = false
    })
  }

  showPlayer(videoId) {
    // Remove the fallback player so hidden or late-loading replay audio stops.
    const fragment = this.templateTarget.content.cloneNode(true)
    const player = fragment.querySelector("media-player")
    const source = `${this.baseUrlValue}/${videoId}/manifest/video.m3u8`
    player.setAttribute("src", source)
    player.setAttribute("data-video-player-source-value", source)
    this.placeholderTarget.replaceChildren()
    this.mountTarget.replaceChildren(fragment)
    this.videoId = videoId
    this.retryPlayback = false
  }

  restorePlaceholder() {
    if (!this.placeholderTarget.childElementCount) {
      this.placeholderTarget.replaceChildren(this.placeholderTemplateTarget.content.cloneNode(true))
    }
  }

  clearPlayer() {
    this.mountTarget.replaceChildren()
    this.videoId = null
    this.retryPlayback = false
  }
}
